import { DataViewOption } from '@va/constants';
import { getSupportCenterDomain } from '@va/dashboard/selectors/customization';
import { isMobileDevice } from '@va/dashboard/selectors/ui';
import { useLocale, useTranslate } from '@va/localization';
import {
  EngagementBar,
  Paragraph,
  ReportBlock,
  StackedBarChart,
  StackedBarChartDataType,
  externalStackedBarTooltip,
  paragraphSizes,
} from '@va/ui/design-system';
import { addNumberSeparator } from '@va/util/helpers';
import { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';

type TrafficChartCardProps = {
  data: StackedBarChartDataType | undefined;
  isLoading: boolean;
  baseKey: string;
};

export const chartColor = {
  high: '#06A25A',
  medium: '#F66F1E',
  low: '#EA2A0C',
};

const TrafficChartCard = ({ data, isLoading, baseKey }: TrafficChartCardProps) => {
  const supportCenterDomain = useSelector(getSupportCenterDomain);
  const hasData = useMemo(() => data?.datasets[0].data.some((item) => item !== 0), [data?.datasets]);

  const isMobile = useSelector(isMobileDevice);
  const { locale } = useLocale();
  const translate = useTranslate();

  return (
    <ReportBlock
      titleTooltip={translate(`card.${baseKey}.footer.tooltip`, { supportCenterDomain })}
      title={translate(`card.${baseKey}.title`)}
      visualization={{ selectedView: DataViewOption.barChart }}
    >
      <div className='flex flex-col h-full min-h-350'>
        <EngagementBar colors={chartColor} className='max-w-300 w-full ml-auto mb-3' />
        {data && !hasData && !isLoading && (
          <div className='grow flex items-center justify-center -translate-y-7'>
            <Paragraph size={paragraphSizes.large}>{translate('table.noData')}</Paragraph>
          </div>
        )}
        {isLoading && <Skeleton count={1} className='min-h-[18.75rem] rounded-18' />}

        {data && hasData && !isLoading && (
          <StackedBarChart
            tooltipFn={(tooltipCtx) => {
              return externalStackedBarTooltip(tooltipCtx);
            }}
            canvasId={`${baseKey}-stacked-bar-chart`}
            chartData={data}
            configuration={{
              maintainAspectRatio: false,
              aspectRatio: 1,
              limitTicks: isMobile,
              xAxisMaxRotation: 0,
              yAxisCallback: (value) => {
                return addNumberSeparator(value, locale, { notation: 'compact', compactDisplay: 'short' });
              },
            }}
          />
        )}
      </div>
    </ReportBlock>
  );
};

export default TrafficChartCard;
